import React from 'react';
import { IconAlertTriangle, IconAlertTriangleCheck } from '@repo/svgs';
import { cn } from '@/lib/cn';
import { toSentenceCase } from '@/utils/textTransform';

export type OpenViolationCellSeverity = 'low' | 'medium' | 'high' | 'highest';

export interface OpenViolationCellProps {
  /** The violation title/status */
  title: string;
  /** The date of the violation */
  date: string;
  /** The detailed description of the violation */
  description?: string;
  /** The severity level which determines icon color */
  isOpen: boolean;

  severity?: OpenViolationCellSeverity;
  /** Whether to show the description */
  showDescription?: boolean;
  /** Additional className for styling */
  className?: string;
}

const getSeverityIconClass = (severity: OpenViolationCellSeverity) => {
  switch (severity) {
    case 'low':
      return 'icon-caution'; // Yellow/amber
    case 'medium':
      return 'icon-caution-mid'; // Orange
    case 'high':
      return 'icon-alert'; // Red-orange
    case 'highest':
      return 'icon-alert-mid'; // Red
    default:
      return 'icon-caution-mid';
  }
};

/**
 * OpenViolationCell component for displaying individual violation details in modals
 * Based on the Figma design system component
 */
export function OpenViolationCell({
  title,
  date,
  description,
  severity = 'medium',
  showDescription = true,
  className,
  isOpen,
}: OpenViolationCellProps) {
  return (
    <div className={cn('flex flex-col gap-2 px-2 py-3 rounded-xl bg-primary', className)}>
      {/* Main content with icon and text */}
      <div className="flex items-center gap-4">
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          {isOpen ? (
            <IconAlertTriangle className={cn('w-5 h-5', getSeverityIconClass(severity))} />
          ) : (
            <IconAlertTriangleCheck className={cn('w-5 h-5', getSeverityIconClass(severity))} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-1">
            <p className="text-body-2 text-primary">{title}</p>
            <p className="text-caption text-secondary">{date}</p>
          </div>
        </div>
      </div>

      {/* Description with left padding to align with text */}
      {showDescription && description && (
        <div className="pl-10">
          <p className="text-caption text-secondary">{toSentenceCase(description)}</p>
        </div>
      )}
    </div>
  );
}
